import { toMinor } from './money';
import { lineAmounts, type AmountsAre } from './sales';

/**
 * Recurring invoices, bills and expenses (document 06 A7): the template form keeps the document
 * as its own form would save it (`document` in POST /recurring-templates), without dates; the
 * server prices it with the Sales or Purchases rules.
 */
export type DocumentTemplateKind = 'invoice' | 'bill' | 'expense';

export const DOCUMENT_TEMPLATE_KINDS: Array<{ kind: DocumentTemplateKind; label: string }> = [
  { kind: 'invoice', label: 'Invoice' },
  { kind: 'bill', label: 'Bill' },
  { kind: 'expense', label: 'Expense' },
];

export const isDocumentTemplateKind = (k: unknown): k is DocumentTemplateKind =>
  k === 'invoice' || k === 'bill' || k === 'expense';

/** Where a document created from a template opens. */
export const documentPath = (kind: DocumentTemplateKind, id: string): string =>
  kind === 'invoice'
    ? `/sales/invoices/${id}`
    : kind === 'bill'
      ? `/expenses/bills/${id}`
      : `/expenses/expenses/${id}`;

export interface TemplateRow {
  key: number;
  itemId: string | null;
  accountId: string | null;
  description: string;
  quantity: string;
  rate: string;
  taxCodeId: string | null;
  /** Purchases: "100" (blank) recoverable, "0" blocked, or between. */
  recoverablePercent: string;
}

export interface DocumentTemplateState {
  contactId: string | null;
  termsDays: number | null;
  placeOfSupply: string;
  amountsAre: AmountsAre;
  reference: string;
  memo: string;
  customerEmail: string;
  paymentAccountId: string | null;
  paymentMethod: string;
  rows: TemplateRow[];
}

let seq = 0;
export const emptyTemplateRow = (taxCodeId: string | null = null): TemplateRow => ({
  key: ++seq,
  itemId: null,
  accountId: null,
  description: '',
  quantity: '',
  rate: '',
  taxCodeId,
  recoverablePercent: '',
});

export const rowUsed = (r: TemplateRow): boolean =>
  Boolean(r.itemId || r.description.trim() || r.rate);

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const idOrNull = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/** Form state from a saved template's `document.form` (or a document form's body). */
export function stateFromForm(form: Record<string, unknown> | null): DocumentTemplateState {
  const lines = Array.isArray(form?.['lines'])
    ? (form['lines'] as Array<Record<string, unknown>>)
    : [];
  const rows = lines.map((l) => ({
    key: ++seq,
    itemId: idOrNull(l['itemId']),
    accountId: idOrNull(l['accountId']),
    description: str(l['description']),
    quantity: str(l['quantity']),
    rate: str(l['unitPrice']),
    taxCodeId: idOrNull(l['taxCodeId']),
    recoverablePercent: str(l['recoverablePercent']),
  }));
  while (rows.length < 2) rows.push(emptyTemplateRow());
  const amountsAre = form?.['amountsAre'];
  return {
    contactId: idOrNull(form?.['contactId']),
    termsDays: typeof form?.['termsDays'] === 'number' ? (form['termsDays'] as number) : null,
    placeOfSupply: str(form?.['placeOfSupply']),
    amountsAre:
      amountsAre === 'inclusive' || amountsAre === 'out_of_scope' ? amountsAre : 'exclusive',
    reference: str(form?.['reference']),
    memo: str(form?.['memo']),
    customerEmail: str(form?.['customerEmail']),
    paymentAccountId: idOrNull(form?.['paymentAccountId']),
    paymentMethod: str(form?.['paymentMethod']),
    rows,
  };
}

/** The `document` of POST /recurring-templates: what the document's own form would send, no dates. */
export function formBody(
  kind: DocumentTemplateKind,
  s: DocumentTemplateState,
): Record<string, unknown> {
  return {
    contactId: s.contactId,
    ...(kind !== 'expense' ? { termsDays: s.termsDays } : {}),
    ...(kind === 'invoice'
      ? { placeOfSupply: s.placeOfSupply || null, customerEmail: s.customerEmail }
      : {}),
    ...(kind === 'expense'
      ? { paymentAccountId: s.paymentAccountId, paymentMethod: s.paymentMethod || null }
      : {}),
    amountsAre: s.amountsAre,
    reference: s.reference,
    memo: s.memo,
    lines: s.rows.filter(rowUsed).map((r) => ({
      itemId: r.itemId,
      accountId: r.accountId,
      description: r.description,
      quantity: r.quantity || '1',
      unitPrice: r.rate || '0',
      taxCodeId: r.taxCodeId,
      ...(kind !== 'invoice' && r.recoverablePercent.trim()
        ? { recoverablePercent: r.recoverablePercent.trim() }
        : {}),
    })),
  };
}

/**
 * Totals as the form shows them (bigint fils, VAT per line rounded half up as on the server).
 * Reverse charge and import codes add no VAT to the vendor's total; the server works out the
 * self-assessed VAT when the template is saved.
 */
export function templateTotals(
  s: DocumentTemplateState,
  rateBp: (taxCodeId: string | null) => { bp: number; charged: boolean },
): { net: bigint; tax: bigint; total: bigint; perRow: Map<number, bigint> } {
  let net = 0n;
  let tax = 0n;
  const perRow = new Map<number, bigint>();
  for (const r of s.rows) {
    if (!rowUsed(r)) continue;
    const { bp, charged } = rateBp(r.taxCodeId);
    const a = lineAmounts(r.quantity || '1', toMinor(r.rate) ?? 0n, charged ? bp : 0, s.amountsAre);
    perRow.set(r.key, s.amountsAre === 'inclusive' ? a.gross : a.net);
    net += a.net;
    tax += a.tax;
  }
  return { net, tax, total: net + tax, perRow };
}

/** "Make recurring" hands the document form's body to the template form (in memory, one use). */
export interface DocumentTemplateSeed {
  kind: DocumentTemplateKind;
  form: Record<string, unknown>;
}
let seed: DocumentTemplateSeed | null = null;
export const setDocumentTemplateSeed = (s: DocumentTemplateSeed | null): void => {
  seed = s;
};
export const takeDocumentTemplateSeed = (
  kind: DocumentTemplateKind,
): DocumentTemplateSeed | null => {
  const s = seed && seed.kind === kind ? seed : null;
  seed = null;
  return s;
};
